import { useState, useEffect, useCallback } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import {
  opsAiGetConfig,
  opsAiSaveConfig,
  opsAiGetRuns,
  opsAiGetRun,
  opsAiRunNow,
  getE2eRuns,
} from '../../lib/adminApi';

const NAVY   = '#0a2540';
const ORANGE = '#F37428';

const DEFAULT_SYSTEM_PROMPT = `You are OPS AI, an infrastructure monitoring assistant for MasterFileAI. You analyze operational metrics from Azure SQL, Azure Functions, Storage Queues, and the Claude API. Your job is to produce a clear, concise daily digest for the engineering team.

Classify the overall status as:
- OK: All metrics within normal thresholds
- REVIEW: One or more metrics approaching warning thresholds or minor anomalies detected
- CRITICAL: One or more metrics exceeding critical thresholds or significant issues detected

Be factual and precise. Use numbers. Call out trends, not just point-in-time values. Flag anything unusual even if it hasn't crossed a threshold.`;

const DEFAULT_REPORT_PROMPT = `Based on the metrics data provided, generate a structured daily operations report in JSON format with the following shape:

{
  "status": "OK | REVIEW | CRITICAL",
  "summary": "One sentence executive summary",
  "alerts": [{ "severity": "warning|critical", "metric": "...", "value": "...", "message": "..." }],
  "sections": {
    "sql": { "headline": "...", "notes": "..." },
    "functions": { "headline": "...", "notes": "..." },
    "queue": { "headline": "...", "notes": "..." },
    "costs": { "headline": "...", "notes": "..." },
    "claude_api": { "headline": "...", "notes": "..." }
  },
  "recommendations": ["..."],
  "cost_forecast": { "month_end_estimate_usd": 0, "confidence": "low|medium|high", "note": "..." }
}`;

const SAMPLE_RUN_OUTPUT = {
  status: 'REVIEW',
  summary: 'Azure Functions error rate slightly elevated; all other systems nominal.',
  alerts: [
    { severity: 'warning', metric: 'Function Error Rate', value: '3.2%', message: 'Error rate above 3% warning threshold. Top error: mfai-processWorker timeout.' }
  ],
  sections: {
    sql: { headline: 'SQL healthy — DTU avg 12%, storage 18%', notes: 'No deadlocks in the past 24h. Peak DTU 41% at 14:32 UTC during batch job.' },
    functions: { headline: 'Error rate 3.2% — above warning threshold', notes: 'mfai-processWorker had 4 timeouts. Avg response 340ms, p95 880ms.' },
    queue: { headline: 'Queue depth nominal — 0 msgs', notes: 'mfai-process queue clear. No backlog.' },
    costs: { headline: 'MTD $142 — on track', notes: 'Daily burn $8.40. Prior month same period was $9.10. 7% below trend.' },
    claude_api: { headline: 'Claude API healthy — 82% rate limit headroom', notes: 'Token usage 1.2M tokens today. MTD cost $18.40.' }
  },
  recommendations: [
    'Investigate mfai-processWorker timeout — consider increasing function timeout from 5m to 10m.',
    'Monitor error rate over next 24h; escalate if it exceeds 5%.'
  ],
  cost_forecast: { month_end_estimate_usd: 260, confidence: 'high', note: 'Based on 6-month burn trend.' }
};

const AZURE_DATA_INPUTS = [
  { key: 'sql_dtu_cpu',        label: 'SQL DTU & CPU utilization',   tokens: 120, checked: true },
  { key: 'sql_storage_conn',   label: 'SQL storage & connections',   tokens: 80,  checked: true },
  { key: 'sql_deadlocks',      label: 'SQL deadlocks & timeouts',    tokens: 60,  checked: true },
  { key: 'fn_exec_counts',     label: 'Function execution counts',   tokens: 90,  checked: true },
  { key: 'fn_errors',          label: 'Function error rate & names', tokens: 140, checked: true },
  { key: 'fn_response_times',  label: 'Function response times',     tokens: 80,  checked: true },
  { key: 'queue_depth',        label: 'Storage queue depth',         tokens: 40,  checked: true },
  { key: 'resource_inventory', label: 'Resource inventory',          tokens: 200, checked: true },
  { key: 'cost_mtd',           label: 'Cost MTD per resource',       tokens: 180, checked: true },
  { key: 'daily_burn',         label: 'Daily burn + 6-month history',tokens: 220, checked: true },
  { key: 'swa_metrics',        label: 'Static Web App metrics',      tokens: 80,  checked: false },
];

const CLAUDE_DATA_INPUTS = [
  { key: 'claude_tokens',    label: 'Token usage',           tokens: 80, checked: true },
  { key: 'claude_cost',      label: 'Daily & MTD cost',      tokens: 60, checked: true },
  { key: 'claude_ratelimit', label: 'Rate limit headroom',   tokens: 40, checked: true },
];

const WEB_SEARCH_DATA_INPUTS = [
  { key: 'ws_health',        label: 'Azure Service Health findings',       tokens: 300, checked: true },
  { key: 'ws_pricing',       label: 'Azure pricing findings',              tokens: 200, checked: true },
  { key: 'ws_anthropic',     label: 'Anthropic model/pricing findings',    tokens: 200, checked: true },
  { key: 'ws_deprecations',  label: 'Azure deprecation notices',          tokens: 300, checked: false },
];

const PROMPT_BASE_TOKENS = 340;
const CONTEXT_WINDOW     = 200000;

function Toggle({ on, onToggle, disabled }) {
  return (
    <div
      onClick={disabled ? undefined : onToggle}
      style={{
        width: 36, height: 20, borderRadius: 10,
        background: on ? ORANGE : '#e2e8f0',
        position: 'relative',
        cursor: disabled ? 'not-allowed' : 'pointer',
        flexShrink: 0, transition: 'background 0.2s', opacity: disabled ? 0.5 : 1,
      }}
    >
      <div style={{
        position: 'absolute', width: 16, height: 16, background: '#fff',
        borderRadius: '50%', top: 2, left: on ? 18 : 2,
        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase',
      letterSpacing: '0.08em', marginBottom: 12 }}>
      {children}
    </div>
  );
}

function Card({ children, style }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
      padding: '20px 22px', ...style }}>
      {children}
    </div>
  );
}

function SaveBtn({ saving, saved, onClick, label = 'Save' }) {
  return (
    <button onClick={onClick} disabled={saving}
      style={{ background: saving ? '#f1f5f9' : ORANGE, color: saving ? '#94a3b8' : '#fff',
        border: 'none', borderRadius: 8, padding: '7px 18px', fontSize: 13, fontWeight: 600,
        cursor: saving ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
      {saving ? '⟳ Saving…' : saved ? '✓ Saved' : label}
    </button>
  );
}

function StatusBadge({ status }) {
  if (!status) return null;
  const map = {
    OK:       { bg: '#f0fdf4', color: '#15803d', label: '✓ OK' },
    REVIEW:   { bg: '#fffbeb', color: '#b45309', label: '⚠ REVIEW' },
    CRITICAL: { bg: '#fef2f2', color: '#b91c1c', label: '🔴 CRITICAL' },
  };
  const s = map[status] || map.OK;
  return (
    <span style={{ background: s.bg, color: s.color, fontSize: 11, fontWeight: 700,
      padding: '3px 10px', borderRadius: 10, border: `1px solid ${s.color}33` }}>
      {s.label}
    </span>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function fmtDateShort(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function OpsAI() {
  const [config,       setConfig]       = useState({});
  const [loading,      setLoading]      = useState(true);
  const [activeTab,    setActiveTab]    = useState('dashboard');
  const [runs,         setRuns]         = useState([]);
  const [runLoading,   setRunLoading]   = useState(false);
  const [runResult,    setRunResult]    = useState(null);
  const [viewRun,      setViewRun]      = useState(null);
  const [viewRunData,  setViewRunData]  = useState(null);
  const [viewRunLoading, setViewRunLoading] = useState(false);
  const [dashData,     setDashData]     = useState(null);
  const [dashLoading,  setDashLoading]  = useState(false);
  const [e2eRuns,      setE2eRuns]      = useState([]);

  // Settings tab state
  const [enabled,       setEnabled]       = useState(true);
  const [frequency,     setFrequency]     = useState('daily');
  const [runAt,         setRunAt]         = useState('6');
  const [resourceGroup, setResourceGroup] = useState('rg-masterfileai');
  const [savingGeneral, setSavingGeneral] = useState(false);
  const [savedGeneral,  setSavedGeneral]  = useState(false);

  const [notifyEmails,    setNotifyEmails]    = useState([]);
  const [emailInput,      setEmailInput]      = useState('');
  const [subjectPrefix,   setSubjectPrefix]   = useState('OPS AI — Daily Report —');
  const [sendOnOK,        setSendOnOK]        = useState(false);
  const [sendOnReview,    setSendOnReview]    = useState(true);
  const [sendOnCritical,  setSendOnCritical]  = useState(true);
  const [fromName,        setFromName]        = useState('OPS AI — MasterFileAI');
  const [savingNotify,    setSavingNotify]    = useState(false);
  const [savedNotify,     setSavedNotify]     = useState(false);

  // Thresholds tab
  const [thresholds, setThresholds] = useState({
    sql_dtu_avg_warn: '60', sql_dtu_avg_crit: '85',
    sql_dtu_peak_warn: '80', sql_dtu_peak_crit: '95',
    sql_storage_warn: '70', sql_storage_crit: '90',
    sql_deadlocks_warn: '5', sql_deadlocks_crit: '20',
    fn_error_rate_warn: '3', fn_error_rate_crit: '10',
    fn_avg_resp_warn: '500', fn_avg_resp_crit: '2000',
    fn_p95_resp_warn: '1500', fn_p95_resp_crit: '5000',
    queue_depth_warn: '50', queue_depth_crit: '200',
    cost_mtd_pct_warn: '15', cost_mtd_pct_crit: '30',
    cost_daily_azure_warn: '12', cost_daily_azure_crit: '20',
    cost_daily_claude_warn: '5', cost_daily_claude_crit: '12',
    claude_rate_limit_warn: '75', claude_rate_limit_crit: '90',
  });
  const [savingThresh, setSavingThresh] = useState({});
  const [savedThresh,  setSavedThresh]  = useState({});

  // Intelligence tab
  const [wsAzureHealth,    setWsAzureHealth]    = useState(true);
  const [wsAzurePricing,   setWsAzurePricing]   = useState(true);
  const [wsAnthropicNews,  setWsAnthropicNews]  = useState(true);
  const [wsDeprecations,   setWsDeprecations]   = useState(true);
  const [aiUpgradeRec,     setAiUpgradeRec]     = useState(true);
  const [aiCostTrend,      setAiCostTrend]      = useState(true);
  const [aiCostForecast,   setAiCostForecast]   = useState(true);
  const [anthropicKey,     setAnthropicKey]     = useState('');
  const [showApiKey,       setShowApiKey]       = useState(false);
  const [savingIntel,      setSavingIntel]      = useState(false);
  const [savedIntel,       setSavedIntel]       = useState(false);

  // Prompt tab
  const [dataInputs, setDataInputs] = useState(() => {
    const az = AZURE_DATA_INPUTS.map(i => ({ ...i }));
    const cl = CLAUDE_DATA_INPUTS.map(i => ({ ...i }));
    const ws = WEB_SEARCH_DATA_INPUTS.map(i => ({ ...i }));
    return { azure: az, claude: cl, webSearch: ws };
  });
  const [systemPrompt,      setSystemPrompt]      = useState(DEFAULT_SYSTEM_PROMPT);
  const [reportPrompt,      setReportPrompt]      = useState(DEFAULT_REPORT_PROMPT);
  const [promptSaving,      setPromptSaving]      = useState(false);
  const [promptSaved,       setPromptSaved]       = useState(false);
  const [testRunning,       setTestRunning]       = useState(false);
  const [testOutput,        setTestOutput]        = useState(null);
  const [savingInputs,      setSavingInputs]      = useState(false);
  const [savedInputs,       setSavedInputs]       = useState(false);

  const intelligenceOn = wsAzureHealth || wsAzurePricing || wsAnthropicNews || wsDeprecations;

  const tokenCount = (() => {
    let total = PROMPT_BASE_TOKENS;
    dataInputs.azure.forEach(i => { if (i.checked) total += i.tokens; });
    dataInputs.claude.forEach(i => { if (i.checked) total += i.tokens; });
    if (intelligenceOn) dataInputs.webSearch.forEach(i => { if (i.checked) total += i.tokens; });
    return total;
  })();

  const load = useCallback(async () => {
    try {
      const [cfg, runsData, e2eData] = await Promise.all([
        opsAiGetConfig().catch(() => ({})),
        opsAiGetRuns().catch(() => []),
        getE2eRuns().catch(() => []),
      ]);
      setE2eRuns(Array.isArray(e2eData) ? e2eData : []);
      const c = cfg?.config || {};
      setConfig(c);

      if (c.enabled !== undefined)       setEnabled(!!c.enabled);
      if (c.frequency)                   setFrequency(c.frequency);
      if (c.run_at !== undefined)        setRunAt(String(c.run_at));
      if (c.resource_group)             setResourceGroup(c.resource_group);
      if (c.notify_emails)              setNotifyEmails(c.notify_emails);
      if (c.subject_prefix)             setSubjectPrefix(c.subject_prefix);
      if (c.send_on_ok !== undefined)   setSendOnOK(!!c.send_on_ok);
      if (c.send_on_review !== undefined) setSendOnReview(!!c.send_on_review);
      if (c.send_on_critical !== undefined) setSendOnCritical(!!c.send_on_critical);
      if (c.from_name)                  setFromName(c.from_name);
      if (c.thresholds)                 setThresholds(t => ({ ...t, ...c.thresholds }));
      if (c.system_prompt)              setSystemPrompt(c.system_prompt);
      if (c.report_prompt)              setReportPrompt(c.report_prompt);
      if (c.ws_azure_health !== undefined)   setWsAzureHealth(!!c.ws_azure_health);
      if (c.ws_azure_pricing !== undefined)  setWsAzurePricing(!!c.ws_azure_pricing);
      if (c.ws_anthropic_news !== undefined) setWsAnthropicNews(!!c.ws_anthropic_news);
      if (c.ws_deprecations !== undefined)   setWsDeprecations(!!c.ws_deprecations);
      if (c.ai_upgrade_rec !== undefined)    setAiUpgradeRec(!!c.ai_upgrade_rec);
      if (c.ai_cost_trend !== undefined)     setAiCostTrend(!!c.ai_cost_trend);
      if (c.ai_cost_forecast !== undefined)  setAiCostForecast(!!c.ai_cost_forecast);

      setRuns(Array.isArray(runsData) ? runsData : (runsData?.runs || []));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const run = runs[0];
    if (!run) return;
    setDashLoading(true);
    opsAiGetRun(run.run_id)
      .then(d => setDashData(d))
      .catch(() => {})
      .finally(() => setDashLoading(false));
  }, [runs]);

  function flashSaved(setter) {
    setter(true);
    setTimeout(() => setter(false), 2500);
  }

  async function saveGeneral() {
    setSavingGeneral(true);
    try {
      await Promise.all([
        opsAiSaveConfig('enabled', enabled),
        opsAiSaveConfig('frequency', frequency),
        opsAiSaveConfig('run_at', parseInt(runAt, 10)),
        opsAiSaveConfig('resource_group', resourceGroup),
      ]);
      flashSaved(setSavedGeneral);
    } catch (e) { alert(e.message); }
    finally { setSavingGeneral(false); }
  }

  async function saveNotifications() {
    setSavingNotify(true);
    try {
      await opsAiSaveConfig('notifications', {
        emails: notifyEmails,
        subject_prefix: subjectPrefix,
        send_ok: sendOnOK,
        send_review: sendOnReview,
        send_critical: sendOnCritical,
        from_name: fromName,
      });
      flashSaved(setSavedNotify);
    } catch (e) { alert(e.message); }
    finally { setSavingNotify(false); }
  }

  function addEmail() {
    const email = emailInput.trim().toLowerCase();
    if (!email || notifyEmails.includes(email)) { setEmailInput(''); return; }
    setNotifyEmails(prev => [...prev, email]);
    setEmailInput('');
  }

  function removeEmail(email) {
    setNotifyEmails(prev => prev.filter(e => e !== email));
  }

  async function saveThresholdGroup(groupKey, keys) {
    setSavingThresh(s => ({ ...s, [groupKey]: true }));
    try {
      const subset = {};
      keys.forEach(k => { subset[k] = thresholds[k]; });
      await opsAiSaveConfig('thresholds', { ...((config.thresholds) || {}), ...subset });
      setSavedThresh(s => ({ ...s, [groupKey]: true }));
      setTimeout(() => setSavedThresh(s => ({ ...s, [groupKey]: false })), 2500);
    } catch (e) { alert(e.message); }
    finally { setSavingThresh(s => ({ ...s, [groupKey]: false })); }
  }

  async function saveIntelligence() {
    setSavingIntel(true);
    try {
      await Promise.all([
        opsAiSaveConfig('ws_azure_health', wsAzureHealth),
        opsAiSaveConfig('ws_azure_pricing', wsAzurePricing),
        opsAiSaveConfig('ws_anthropic_news', wsAnthropicNews),
        opsAiSaveConfig('ws_deprecations', wsDeprecations),
        opsAiSaveConfig('ai_upgrade_rec', aiUpgradeRec),
        opsAiSaveConfig('ai_cost_trend', aiCostTrend),
        opsAiSaveConfig('ai_cost_forecast', aiCostForecast),
        ...(anthropicKey ? [opsAiSaveConfig('anthropic_admin_key', anthropicKey)] : []),
      ]);
      flashSaved(setSavedIntel);
    } catch (e) { alert(e.message); }
    finally { setSavingIntel(false); }
  }

  async function savePrompt() {
    setPromptSaving(true);
    try {
      await Promise.all([
        opsAiSaveConfig('system_prompt', systemPrompt),
        opsAiSaveConfig('report_prompt', reportPrompt),
      ]);
      flashSaved(setPromptSaved);
    } catch (e) { alert(e.message); }
    finally { setPromptSaving(false); }
  }

  async function saveDataInputs() {
    setSavingInputs(true);
    try {
      const checked = {
        azure: dataInputs.azure.filter(i => i.checked).map(i => i.key),
        claude: dataInputs.claude.filter(i => i.checked).map(i => i.key),
        webSearch: dataInputs.webSearch.filter(i => i.checked).map(i => i.key),
      };
      await opsAiSaveConfig('data_inputs', checked);
      flashSaved(setSavedInputs);
    } catch (e) { alert(e.message); }
    finally { setSavingInputs(false); }
  }

  async function runTest() {
    setTestRunning(true);
    setTestOutput(null);
    await new Promise(r => setTimeout(r, 1500));
    setTestOutput(SAMPLE_RUN_OUTPUT);
    setTestRunning(false);
  }

  async function handleRunNow() {
    setRunLoading(true);
    setRunResult(null);
    try {
      const res = await opsAiRunNow();
      setRunResult(res || { status: 'OK', message: 'Run triggered successfully.' });
      await load();
    } catch (e) {
      setRunResult({ error: e.message });
    } finally {
      setRunLoading(false);
    }
  }

  async function openRunDetail(run) {
    setViewRun(run);
    setViewRunData(null);
    setViewRunLoading(true);
    try {
      const detail = await opsAiGetRun(run.run_id);
      setViewRunData(detail);
    } catch (e) {
      setViewRunData({ error: e.message });
    } finally {
      setViewRunLoading(false);
    }
  }

  function toggleDataInput(section, key) {
    setDataInputs(prev => ({
      ...prev,
      [section]: prev[section].map(i => i.key === key ? { ...i, checked: !i.checked } : i),
    }));
  }

  const lastRun = runs[0];

  if (loading) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: '#f0f5fb' }}>
        <AdminSidebar active="ops-ai" />
        <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ color: '#94a3b8', fontSize: 14 }}>Loading OPS AI…</div>
        </main>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f0f5fb',
      fontFamily: "'Inter', system-ui, sans-serif" }}>
      <AdminSidebar active="ops-ai" />
      <main style={{ flex: 1, overflowY: 'auto' }}>

        {/* ── Hero Banner ── */}
        <div style={{
          background: `linear-gradient(135deg, ${NAVY} 0%, #0f3460 50%, #162d4a 100%)`,
          padding: '32px 36px 28px',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', inset: 0, opacity: 0.04,
            backgroundImage: 'repeating-linear-gradient(0deg,#fff 0px,#fff 1px,transparent 1px,transparent 40px),repeating-linear-gradient(90deg,#fff 0px,#fff 1px,transparent 1px,transparent 40px)' }} />

          <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 18 }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: 'rgba(243,116,40,0.15)',
                border: '1px solid rgba(243,116,40,0.3)', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 26, flexShrink: 0 }}>
                ⚙️
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#fff' }}>Hawk</h1>
                  <span style={{ background: 'rgba(243,116,40,0.2)', color: ORANGE, fontSize: 9,
                    fontWeight: 700, letterSpacing: '0.1em', padding: '3px 8px', borderRadius: 10,
                    textTransform: 'uppercase', border: '1px solid rgba(243,116,40,0.3)' }}>Bot 2</span>
                </div>
                <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)', fontSize: 13, maxWidth: 520, lineHeight: 1.5 }}>
                  Infrastructure monitoring · Daily digest · Azure + Claude API
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0 }}>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', textAlign: 'right' }}>
                Last run: <span style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>
                  {lastRun ? fmtDate(lastRun.run_at) : 'Never'}
                </span>
              </div>
              {enabled ? (
                <span style={{ background: 'rgba(52,211,153,0.15)', color: '#34d399', fontSize: 11,
                  fontWeight: 700, padding: '4px 12px', borderRadius: 10,
                  border: '1px solid rgba(52,211,153,0.3)' }}>✓ Active</span>
              ) : (
                <span style={{ background: 'rgba(148,163,184,0.15)', color: '#94a3b8', fontSize: 11,
                  fontWeight: 700, padding: '4px 12px', borderRadius: 10,
                  border: '1px solid rgba(148,163,184,0.3)' }}>Paused</span>
              )}
            </div>
          </div>
        </div>

        {/* ── Run Result Banner ── */}
        {runResult && (
          <div style={{
            background: runResult.error ? '#fef2f2' : '#f0fdf4',
            border: `1px solid ${runResult.error ? '#fca5a5' : '#86efac'}`,
            borderRadius: 0, padding: '12px 36px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: runResult.error ? '#b91c1c' : '#15803d' }}>
              {runResult.error ? `✕ Error: ${runResult.error}` : `✓ ${runResult.message || 'Run triggered successfully'}`}
            </span>
            <button onClick={() => setRunResult(null)}
              style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 16 }}>×</button>
          </div>
        )}

        <div style={{ padding: '24px 36px 100px' }}>

          {/* ── Tabs ── */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 24,
            background: '#e2e8f0', borderRadius: 10, padding: 4, width: 'fit-content' }}>
            {[
              ['dashboard',   'Dashboard'],
              ['settings',    'Settings'],
              ['thresholds',  'Thresholds'],
              ['intelligence','Intelligence'],
              ['prompt',      'Prompt'],
              ['history',     'Run History'],
            ].map(([key, label]) => (
              <button key={key}
                onClick={() => setActiveTab(key)}
                style={{
                  padding: '7px 18px', border: 'none', borderRadius: 8, fontSize: 13,
                  fontWeight: activeTab === key ? 700 : 500, cursor: 'pointer',
                  background: activeTab === key ? '#fff' : 'transparent',
                  color: activeTab === key ? NAVY : '#64748b',
                  boxShadow: activeTab === key ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.15s',
                }}>
                {label}
              </button>
            ))}
          </div>

          {/* ═══════════════════════════════════════════════════════
              TAB: DASHBOARD
          ═══════════════════════════════════════════════════════ */}
          {activeTab === 'dashboard' && (() => {
            if (dashLoading) return (
              <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>Loading dashboard…</div>
            );
            if (!dashData || runs.length === 0) return (
              <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>📊</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: NAVY, marginBottom: 8 }}>No runs yet</div>
                <div style={{ fontSize: 13 }}>Hit Run Now to generate your first report</div>
              </div>
            );

            const status = dashData.status || 'OK';
            const statusColor = { OK: '#10b981', REVIEW: '#f59e0b', CRITICAL: '#ef4444' }[status] || '#10b981';
            const m = dashData.metrics_json || {};
            const alerts = dashData.alerts_json || [];
            const ai = dashData.claude_output || {};
            const cost = m.cost || {};
            const resources = m.resources || {};
            const alertWarn = dashData.alert_count_warn || 0;
            const alertCrit = dashData.alert_count_crit || 0;
            const alertTotal = alertWarn + alertCrit;

            function metricDot(val, warnKey, critKey) {
              if (val == null) return '#cbd5e1';
              const warn = parseFloat(config.thresholds?.[warnKey]);
              const crit = parseFloat(config.thresholds?.[critKey]);
              if (!isNaN(crit) && val >= crit) return '#ef4444';
              if (!isNaN(warn) && val >= warn) return '#f59e0b';
              return '#10b981';
            }

            const history = cost.history_6m || [];
            const maxBar = Math.max(...history.map(h => h.cost || 0), 1);

            const fnTotal = m.functions?.total_count;
            const fnErrors = m.functions?.error_count;
            const fnErrRate = (fnTotal > 0 && fnErrors != null) ? ((fnErrors / fnTotal) * 100) : null;

            function MRow({ label, val, dot }) {
              return (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '6px 0', borderBottom: '1px solid #f8fafc' }}>
                  <span style={{ fontSize: 12, color: '#374151', display: 'flex', alignItems: 'center', gap: 7 }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: val != null ? dot : '#cbd5e1',
                      flexShrink: 0, display: 'inline-block' }} />
                    {label}
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: val != null ? NAVY : '#94a3b8' }}>{val ?? '—'}</span>
                </div>
              );
            }

            function SecHdr({ children }) {
              return (
                <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase',
                  letterSpacing: '0.08em', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid #f1f5f9' }}>
                  {children}
                </div>
              );
            }

            return (
              <div style={{ maxWidth: 980 }}>
                {/* Status strip */}
                <div style={{ height: 4, background: statusColor, borderRadius: 4, marginBottom: 20 }} />

                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: NAVY }}>Latest Report</div>
                    <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{fmtDateShort(dashData.run_at)}</div>
                  </div>
                  <StatusBadge status={status} />
                </div>

                {/* E2E Health */}
                {(() => {
                  if (e2eRuns.length === 0) return null;
                  const last = e2eRuns[0];
                  const passed = last.status === 'pass';
                  const last7  = e2eRuns.slice(0, 7);
                  return (
                    <div style={{ background: passed ? '#f0fdf4' : '#fef2f2',
                      border: `1px solid ${passed ? '#bbf7d0' : '#fca5a5'}`,
                      borderRadius: 12, padding: '14px 18px', marginBottom: 20,
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 18 }}>{passed ? '✓' : '✕'}</span>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: passed ? '#15803d' : '#b91c1c' }}>
                            E2E Tests — {passed ? 'Passing' : 'FAILING'}
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                            Last run {fmtDate(last.run_at)}
                            {last.failed_step ? ` · Failed: ${last.failed_step}` : ''}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {last7.map((r, i) => (
                            <div key={i} title={`${fmtDate(r.run_at)}: ${r.status}`} style={{
                              width: 10, height: 10, borderRadius: '50%',
                              background: r.status === 'pass' ? '#22c55e' : '#ef4444',
                            }} />
                          ))}
                        </div>
                        {last.run_url && (
                          <a href={last.run_url} target="_blank" rel="noreferrer"
                            style={{ fontSize: 11, color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}>
                            View run →
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Summary cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
                  {[
                    {
                      label: 'Alerts',
                      value: alertTotal,
                      sub: alertCrit > 0 ? `${alertCrit} critical` : alertWarn > 0 ? `${alertWarn} warning` : 'All clear',
                      color: alertCrit > 0 ? '#b91c1c' : alertWarn > 0 ? '#b45309' : '#15803d',
                      bg: alertCrit > 0 ? '#fef2f2' : alertWarn > 0 ? '#fffbeb' : '#f0fdf4',
                      border: alertCrit > 0 ? '#fca5a5' : alertWarn > 0 ? '#fcd34d' : '#bbf7d0',
                    },
                    { label: 'Resources', value: resources.count ?? '—', sub: 'in resource group', color: NAVY, bg: '#fff', border: '#e2e8f0' },
                    { label: 'Daily Cost', value: dashData.daily_cost_usd != null ? `$${Number(dashData.daily_cost_usd).toFixed(2)}` : '—', sub: 'yesterday (Azure)', color: NAVY, bg: '#fff', border: '#e2e8f0' },
                    { label: 'MTD Cost', value: cost.mtd_total != null ? `$${Number(cost.mtd_total).toFixed(2)}` : '—', sub: 'this month (Azure)', color: NAVY, bg: '#fff', border: '#e2e8f0' },
                  ].map((card, i) => (
                    <div key={i} style={{ background: card.bg, border: `1px solid ${card.border}`,
                      borderRadius: 12, padding: '16px 18px', textAlign: 'center' }}>
                      <div style={{ fontSize: 24, fontWeight: 800, color: card.color, lineHeight: 1 }}>{card.value}</div>
                      <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase',
                        letterSpacing: '0.06em', marginTop: 4 }}>{card.label}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>{card.sub}</div>
                    </div>
                  ))}
                </div>

                {/* Alerts */}
                {alerts.length > 0 && (
                  <div style={{ marginBottom: 24 }}>
                    <SecHdr>Alerts ({alerts.length})</SecHdr>
                    {alerts.map((alert, i) => (
                      <div key={i} style={{
                        display: 'flex', gap: 10, padding: '11px 14px', borderRadius: 8, marginBottom: 8,
                        background: alert.severity === 'critical' ? '#fef2f2' : '#fffbeb',
                        border: `1px solid ${alert.severity === 'critical' ? '#fca5a5' : '#fde68a'}`,
                      }}>
                        <span style={{ fontSize: 13, flexShrink: 0 }}>{alert.severity === 'critical' ? '🔴' : '⚠️'}</span>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>
                            {alert.title || alert.metric || ''}
                            {alert.value ? `: ${alert.value}` : ''}
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, lineHeight: 1.5 }}>{alert.detail || alert.message || ''}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Metrics grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
                  <Card>
                    <SecHdr>Azure SQL</SecHdr>
                    {ai.sections?.sql?.headline && <div style={{ fontSize: 12, color: '#475569', marginBottom: 10, lineHeight: 1.5 }}>{ai.sections.sql.headline}</div>}
                    <MRow label="DTU Avg"      val={m.sql?.avg_dtu_pct   != null ? `${m.sql.avg_dtu_pct.toFixed(1)}%`  : null} dot={metricDot(m.sql?.avg_dtu_pct,   'sql_dtu_avg_warn',  'sql_dtu_avg_crit')} />
                    <MRow label="DTU Peak"     val={m.sql?.peak_dtu_pct  != null ? `${m.sql.peak_dtu_pct.toFixed(1)}%` : null} dot={metricDot(m.sql?.peak_dtu_pct,  'sql_dtu_peak_warn', 'sql_dtu_peak_crit')} />
                    <MRow label="Storage"      val={m.sql?.storage_pct   != null ? `${m.sql.storage_pct.toFixed(1)}%`  : null} dot={metricDot(m.sql?.storage_pct,   'sql_storage_warn',  'sql_storage_crit')} />
                    <MRow label="Deadlocks 24h" val={m.sql?.deadlocks_24h != null ? String(m.sql.deadlocks_24h) : null} dot={metricDot(m.sql?.deadlocks_24h, 'sql_deadlocks_warn', 'sql_deadlocks_crit')} />
                  </Card>

                  <Card>
                    <SecHdr>Azure Functions</SecHdr>
                    {ai.sections?.functions?.headline && <div style={{ fontSize: 12, color: '#475569', marginBottom: 10, lineHeight: 1.5 }}>{ai.sections.functions.headline}</div>}
                    <MRow label="Error Rate"   val={fnErrRate  != null ? `${fnErrRate.toFixed(1)}%`                        : null} dot={metricDot(fnErrRate, 'fn_error_rate_warn', 'fn_error_rate_crit')} />
                    <MRow label="Avg Response" val={m.functions?.avg_response_ms != null ? `${m.functions.avg_response_ms.toFixed(0)}ms` : null} dot={metricDot(m.functions?.avg_response_ms, 'fn_avg_resp_warn', 'fn_avg_resp_crit')} />
                    <MRow label="p95 Response" val={m.functions?.p95_response_ms != null ? `${m.functions.p95_response_ms.toFixed(0)}ms` : null} dot={metricDot(m.functions?.p95_response_ms, 'fn_p95_resp_warn', 'fn_p95_resp_crit')} />
                    <MRow label="Total Calls"  val={fnTotal != null ? fnTotal.toLocaleString() : null} dot="#10b981" />
                  </Card>

                  <Card>
                    <SecHdr>Queue &amp; Claude API</SecHdr>
                    {ai.sections?.queue?.headline && <div style={{ fontSize: 12, color: '#475569', marginBottom: 10, lineHeight: 1.5 }}>{ai.sections.queue.headline}</div>}
                    <MRow label="Queue Depth" val={m.queue?.depth != null ? String(m.queue.depth) : null} dot={metricDot(m.queue?.depth, 'queue_depth_warn', 'queue_depth_crit')} />
                    {m.claude_api && Object.entries(m.claude_api).slice(0, 5).map(([k, v]) => (
                      <MRow key={k} label={k.replace(/_/g, ' ')} val={typeof v === 'number' ? v.toLocaleString() : String(v ?? '—')} dot="#10b981" />
                    ))}
                  </Card>
                </div>

                {/* Cost breakdown + chart */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
                  <Card>
                    <SecHdr>MTD Cost by Resource</SecHdr>
                    {cost.by_resource && cost.by_resource.length > 0 ? (
                      <>
                        {[...cost.by_resource].sort((a, b) => b.cost - a.cost).map((r, i) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between',
                            padding: '6px 0', borderBottom: '1px solid #f8fafc', fontSize: 12 }}>
                            <span style={{ color: '#374151' }}>{r.name}</span>
                            <span style={{ fontWeight: 600, color: NAVY }}>${Number(r.cost).toFixed(2)}</span>
                          </div>
                        ))}
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 0',
                          marginTop: 6, borderTop: '2px solid #e2e8f0', fontWeight: 700, fontSize: 14, color: NAVY }}>
                          <span>Total MTD</span>
                          <span>${Number(cost.mtd_total || 0).toFixed(2)}</span>
                        </div>
                        {ai.cost_forecast?.month_end_estimate_usd && (
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6, lineHeight: 1.5 }}>
                            Forecast: ${Number(ai.cost_forecast.month_end_estimate_usd).toFixed(0)}/mo — {ai.cost_forecast.note}
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={{ color: '#94a3b8', fontSize: 12, padding: '16px 0' }}>No cost data available</div>
                    )}
                  </Card>

                  <Card>
                    <SecHdr>Monthly Cost — Last 6 Months (USD)</SecHdr>
                    {history.length > 0 ? (
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 110 }}>
                        {history.map((h, i) => {
                          const pct = Math.max((h.cost / maxBar) * 100, 4);
                          const mo = h.month ? String(h.month) : '';
                          const label = mo ? new Date(mo.slice(0, 7) + '-02').toLocaleString('en-US', { month: 'short' }) : '';
                          return (
                            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <div style={{ width: '100%', display: 'flex', flexDirection: 'column',
                                justifyContent: 'flex-end', height: 80 }}>
                                <div style={{ background: NAVY, borderRadius: '3px 3px 0 0', height: `${pct}%` }}
                                  title={`$${Number(h.cost).toFixed(2)}`} />
                              </div>
                              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4, fontWeight: 600 }}>{label}</div>
                              <div style={{ fontSize: 9, color: '#64748b' }}>${Number(h.cost).toFixed(0)}</div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ color: '#94a3b8', fontSize: 12, padding: '16px 0' }}>No history data yet (runs after 6+ months will populate this)</div>
                    )}
                  </Card>
                </div>

                {/* AI Analysis */}
                {(ai.summary || ai.sections || ai.recommendations?.length > 0) && (
                  <Card style={{ marginBottom: 24 }}>
                    <SecHdr>AI Analysis</SecHdr>
                    {ai.summary && (
                      <div style={{ fontSize: 13, color: '#1e293b', marginBottom: 16, lineHeight: 1.6,
                        padding: '10px 14px', background: '#f8fafc', borderRadius: 8,
                        borderLeft: `3px solid ${statusColor}` }}>
                        {ai.summary}
                      </div>
                    )}
                    {ai.sections && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 16 }}>
                        {Object.entries(ai.sections).map(([key, sec]) => (
                          <div key={key} style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 12px' }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: NAVY, textTransform: 'capitalize', marginBottom: 4 }}>
                              {key.replace(/_/g, ' ')}
                            </div>
                            <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.5 }}>{sec.headline}</div>
                            {sec.notes && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, lineHeight: 1.4 }}>{sec.notes}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                    {ai.recommendations?.length > 0 && (
                      <div>
                        <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginBottom: 8 }}>Recommendations</div>
                        {ai.recommendations.map((r, i) => (
                          <div key={i} style={{ fontSize: 13, color: '#475569', padding: '6px 0',
                            borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                            <span style={{ color: ORANGE, flexShrink: 0, fontWeight: 700 }}>→</span> {r}
                          </div>
                        ))}
                      </div>
                    )}
                  </Card>
                )}
              </div>
            );
          })()}

          {/* ═══════════════════════════════════════════════════════
              TAB: SETTINGS
          ═══════════════════════════════════════════════════════ */}
          {activeTab === 'settings' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 720 }}>

              {/* General Card */}
              <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
                  <div>
                    <SectionLabel>General</SectionLabel>
                    <div style={{ fontSize: 15, fontWeight: 700, color: NAVY, marginTop: -6 }}>Schedule & Scope</div>
                  </div>
                  <SaveBtn saving={savingGeneral} saved={savedGeneral} onClick={saveGeneral} />
                </div>

                {/* Enabled toggle */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>Enabled</div>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Bot will run on schedule when enabled</div>
                  </div>
                  <Toggle on={enabled} onToggle={() => setEnabled(e => !e)} />
                </div>

                {/* Run frequency */}
                <div style={{ padding: '14px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 8 }}>Run Frequency</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {['Hourly', 'Daily', 'Weekly'].map(f => (
                      <button key={f}
                        onClick={() => setFrequency(f.toLowerCase())}
                        style={{
                          padding: '7px 18px', border: `1px solid ${frequency === f.toLowerCase() ? ORANGE : '#e2e8f0'}`,
                          borderRadius: 8, fontSize: 13, fontWeight: frequency === f.toLowerCase() ? 700 : 400,
                          background: frequency === f.toLowerCase() ? '#fff7ed' : '#fff',
                          color: frequency === f.toLowerCase() ? ORANGE : '#64748b', cursor: 'pointer',
                        }}>
                        {f}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Run at time */}
                <div style={{ padding: '14px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 8 }}>Run At (UTC)</div>
                  <select value={runAt} onChange={e => setRunAt(e.target.value)}
                    style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13,
                      background: '#fff', color: '#1e293b', cursor: 'pointer' }}>
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={String(i)}>
                        {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`} UTC
                      </option>
                    ))}
                  </select>
                </div>

                {/* Resource Group */}
                <div style={{ paddingTop: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 8 }}>Azure Resource Group</div>
                  <input value={resourceGroup} onChange={e => setResourceGroup(e.target.value)}
                    style={{ width: '100%', maxWidth: 340, padding: '8px 12px', border: '1px solid #e2e8f0',
                      borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
              </Card>

              {/* Notifications Card */}
              <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
                  <div>
                    <SectionLabel>Notifications</SectionLabel>
                    <div style={{ fontSize: 15, fontWeight: 700, color: NAVY, marginTop: -6 }}>Email Reports</div>
                  </div>
                  <SaveBtn saving={savingNotify} saved={savedNotify} onClick={saveNotifications} />
                </div>

                {/* Email chips */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 8 }}>Send Report To</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10, minHeight: 32 }}>
                    {notifyEmails.map(email => (
                      <div key={email} style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                        background: '#f0f5fb', border: '1px solid #e2e8f0', borderRadius: 20,
                        padding: '4px 12px 4px 14px', fontSize: 12, color: NAVY }}>
                        {email}
                        <button onClick={() => removeEmail(email)}
                          style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer',
                            padding: 0, fontSize: 14, lineHeight: 1 }}>×</button>
                      </div>
                    ))}
                    {notifyEmails.length === 0 && (
                      <span style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic', alignSelf: 'center' }}>
                        No recipients configured
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input value={emailInput} onChange={e => setEmailInput(e.target.value)}
                      onKeyDown={e => (e.key === 'Enter' || e.key === ',') && addEmail()}
                      placeholder="Add email and press Enter…" type="email"
                      style={{ flex: 1, maxWidth: 280, padding: '7px 10px', border: '1px solid #e2e8f0',
                        borderRadius: 7, fontSize: 13 }} />
                    <button onClick={addEmail} disabled={!emailInput.trim()}
                      style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0',
                        borderRadius: 7, padding: '7px 14px', fontSize: 13, cursor: 'pointer' }}>Add</button>
                  </div>
                </div>

                {/* Subject prefix */}
                <div style={{ padding: '14px 0', borderTop: '1px solid #f1f5f9' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 8 }}>Email Subject Prefix</div>
                  <input value={subjectPrefix} onChange={e => setSubjectPrefix(e.target.value)}
                    style={{ width: '100%', maxWidth: 400, padding: '8px 12px', border: '1px solid #e2e8f0',
                      borderRadius: 8, fontSize: 13, boxSizing: 'border-box', marginBottom: 8 }} />
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>
                    Preview: <span style={{ color: '#64748b', fontStyle: 'italic' }}>
                      {subjectPrefix} {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} — OK
                    </span>
                  </div>
                </div>

                {/* Send On toggles */}
                <div style={{ padding: '14px 0', borderTop: '1px solid #f1f5f9' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 12 }}>Send Report On</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {[
                      { label: '✓ OK', sub: 'All metrics within thresholds', val: sendOnOK, set: setSendOnOK },
                      { label: '⚠ REVIEW', sub: 'Warning threshold crossed', val: sendOnReview, set: setSendOnReview },
                      { label: '🔴 CRITICAL', sub: 'Critical threshold exceeded', val: sendOnCritical, set: setSendOnCritical },
                    ].map(({ label, sub, val, set }) => (
                      <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: 420 }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{label}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>{sub}</div>
                        </div>
                        <Toggle on={val} onToggle={() => set(v => !v)} />
                      </div>
                    ))}
                  </div>
                </div>

                {/* From Name */}
                <div style={{ paddingTop: 14, borderTop: '1px solid #f1f5f9' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 8 }}>From Name</div>
                  <input value={fromName} onChange={e => setFromName(e.target.value)}
                    style={{ width: '100%', maxWidth: 340, padding: '8px 12px', border: '1px solid #e2e8f0',
                      borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
              </Card>
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════
              TAB: THRESHOLDS
          ═══════════════════════════════════════════════════════ */}
          {activeTab === 'thresholds' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 800 }}>

              {/* Info box */}
              <div style={{ background: '#f0f5fb', border: '1px solid #c7d7ed', borderRadius: 10, padding: '12px 16px',
                fontSize: 13, color: '#475569', lineHeight: 1.6 }}>
                Metrics exceeding <strong>Warning</strong> trigger ⚠ REVIEW.
                Metrics exceeding <strong>Critical</strong> trigger 🔴 CRITICAL.
              </div>

              {/* Threshold Card Component */}
              {[
                {
                  key: 'sql', title: 'SQL Database', icon: '🗄️',
                  rows: [
                    { label: 'DTU avg', warnKey: 'sql_dtu_avg_warn', critKey: 'sql_dtu_avg_crit', unit: '%' },
                    { label: 'DTU peak', warnKey: 'sql_dtu_peak_warn', critKey: 'sql_dtu_peak_crit', unit: '%' },
                    { label: 'Storage used', warnKey: 'sql_storage_warn', critKey: 'sql_storage_crit', unit: '%' },
                    { label: 'Deadlocks', warnKey: 'sql_deadlocks_warn', critKey: 'sql_deadlocks_crit', unit: 'count' },
                  ],
                  threshKeys: ['sql_dtu_avg_warn','sql_dtu_avg_crit','sql_dtu_peak_warn','sql_dtu_peak_crit','sql_storage_warn','sql_storage_crit','sql_deadlocks_warn','sql_deadlocks_crit'],
                },
                {
                  key: 'functions', title: 'Azure Functions', icon: '⚡',
                  rows: [
                    { label: 'Error Rate', warnKey: 'fn_error_rate_warn', critKey: 'fn_error_rate_crit', unit: '%' },
                    { label: 'Avg Response Time', warnKey: 'fn_avg_resp_warn', critKey: 'fn_avg_resp_crit', unit: 'ms' },
                    { label: 'p95 Response Time', warnKey: 'fn_p95_resp_warn', critKey: 'fn_p95_resp_crit', unit: 'ms' },
                  ],
                  threshKeys: ['fn_error_rate_warn','fn_error_rate_crit','fn_avg_resp_warn','fn_avg_resp_crit','fn_p95_resp_warn','fn_p95_resp_crit'],
                },
                {
                  key: 'queue', title: 'Storage Queue', icon: '📬',
                  rows: [
                    { label: 'Queue Depth — mfai-process', warnKey: 'queue_depth_warn', critKey: 'queue_depth_crit', unit: 'msgs' },
                  ],
                  threshKeys: ['queue_depth_warn','queue_depth_crit'],
                },
                {
                  key: 'costs', title: 'Costs + Claude API', icon: '💰',
                  rows: [
                    { label: 'MTD vs prior month', warnKey: 'cost_mtd_pct_warn', critKey: 'cost_mtd_pct_crit', unit: '% above' },
                    { label: 'Daily Azure cost', warnKey: 'cost_daily_azure_warn', critKey: 'cost_daily_azure_crit', unit: '$/day' },
                    { label: 'Daily Claude cost', warnKey: 'cost_daily_claude_warn', critKey: 'cost_daily_claude_crit', unit: '$/day' },
                    { label: 'Claude rate limit', warnKey: 'claude_rate_limit_warn', critKey: 'claude_rate_limit_crit', unit: '%' },
                  ],
                  threshKeys: ['cost_mtd_pct_warn','cost_mtd_pct_crit','cost_daily_azure_warn','cost_daily_azure_crit','cost_daily_claude_warn','cost_daily_claude_crit','claude_rate_limit_warn','claude_rate_limit_crit'],
                },
              ].map(group => (
                <Card key={group.key}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 18 }}>{group.icon}</span>
                      <div style={{ fontSize: 15, fontWeight: 700, color: NAVY }}>{group.title}</div>
                    </div>
                    <SaveBtn
                      saving={savingThresh[group.key]}
                      saved={savedThresh[group.key]}
                      onClick={() => saveThresholdGroup(group.key, group.threshKeys)}
                    />
                  </div>

                  {/* Header row */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px 160px',
                    gap: 12, marginBottom: 8 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Metric</div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>⚠ Warning</div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.06em' }}>🔴 Critical</div>
                  </div>

                  {group.rows.map((row, i) => (
                    <div key={row.warnKey} style={{ display: 'grid', gridTemplateColumns: '1fr 160px 160px',
                      gap: 12, padding: '10px 0',
                      borderTop: i > 0 ? '1px solid #f1f5f9' : 'none',
                      alignItems: 'center' }}>
                      <div style={{ fontSize: 13, color: '#1e293b' }}>{row.label}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input type="number" value={thresholds[row.warnKey] || ''}
                          onChange={e => setThresholds(t => ({ ...t, [row.warnKey]: e.target.value }))}
                          style={{ width: 80, padding: '6px 10px', border: '1px solid #fbbf24',
                            borderRadius: 7, fontSize: 13, background: '#fffbeb' }} />
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>{row.unit}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input type="number" value={thresholds[row.critKey] || ''}
                          onChange={e => setThresholds(t => ({ ...t, [row.critKey]: e.target.value }))}
                          style={{ width: 80, padding: '6px 10px', border: '1px solid #fca5a5',
                            borderRadius: 7, fontSize: 13, background: '#fef2f2' }} />
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>{row.unit}</span>
                      </div>
                    </div>
                  ))}
                </Card>
              ))}
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════
              TAB: INTELLIGENCE
          ═══════════════════════════════════════════════════════ */}
          {activeTab === 'intelligence' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 720 }}>

              {/* Info box */}
              <div style={{ background: '#f0f5fb', border: '1px solid #c7d7ed', borderRadius: 10, padding: '12px 16px',
                fontSize: 13, color: '#475569', lineHeight: 1.6 }}>
                OPS AI can search the web for live Azure service health, pricing changes, and deprecation notices before generating your report. Claude synthesizes the findings alongside your metrics.
              </div>

              {/* Web Search Card */}
              <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: NAVY }}>Web Search</div>
                    <span style={{ background: '#ede9fe', color: '#7c3aed', fontSize: 10, fontWeight: 700,
                      padding: '2px 8px', borderRadius: 10, letterSpacing: '0.05em' }}>New</span>
                  </div>
                  <SaveBtn saving={savingIntel} saved={savedIntel} onClick={saveIntelligence} />
                </div>

                {[
                  { icon: '🏥', label: 'Azure Service Health', desc: 'Searches for active outages and incidents affecting your Azure region', val: wsAzureHealth, set: setWsAzureHealth },
                  { icon: '💲', label: 'Azure Pricing Updates', desc: 'Checks for pricing changes on your active service tiers', val: wsAzurePricing, set: setWsAzurePricing },
                  { icon: '🤖', label: 'Anthropic Pricing & Model Updates', desc: 'Checks for new Claude models and pricing announcements', val: wsAnthropicNews, set: setWsAnthropicNews },
                  { icon: '📰', label: 'Azure Deprecation Notices', desc: 'Scans for deprecations that may affect your current stack', val: wsDeprecations, set: setWsDeprecations },
                ].map((item, i) => (
                  <div key={item.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px 0', borderTop: i > 0 ? '1px solid #f1f5f9' : 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ fontSize: 20, flexShrink: 0 }}>{item.icon}</span>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{item.label}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{item.desc}</div>
                      </div>
                    </div>
                    <Toggle on={item.val} onToggle={() => item.set(v => !v)} />
                  </div>
                ))}
              </Card>

              {/* AI Analysis Card */}
              <Card>
                <div style={{ fontSize: 15, fontWeight: 700, color: NAVY, marginBottom: 18 }}>AI Analysis</div>

                {[
                  { icon: '📈', label: 'Upgrade Recommendations', desc: 'Suggests DTU tier or storage upgrades based on usage trends', val: aiUpgradeRec, set: setAiUpgradeRec },
                  { icon: '📉', label: 'Cost Trend Narrative', desc: 'Writes a plain-English summary of your spend trajectory', val: aiCostTrend, set: setAiCostTrend },
                  { icon: '🔮', label: 'Month-End Cost Forecast', desc: 'Projects end-of-month Azure + Claude spend with confidence range', val: aiCostForecast, set: setAiCostForecast },
                ].map((item, i) => (
                  <div key={item.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px 0', borderTop: i > 0 ? '1px solid #f1f5f9' : 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ fontSize: 20, flexShrink: 0 }}>{item.icon}</span>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{item.label}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{item.desc}</div>
                      </div>
                    </div>
                    <Toggle on={item.val} onToggle={() => item.set(v => !v)} />
                  </div>
                ))}
              </Card>

              {/* API Keys Card */}
              <Card>
                <div style={{ fontSize: 15, fontWeight: 700, color: NAVY, marginBottom: 6 }}>API Keys</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 18 }}>Stored securely in Azure App Settings — never logged.</div>

                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 6 }}>Anthropic Admin API Key</div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type={showApiKey ? 'text' : 'password'}
                      value={anthropicKey}
                      onChange={e => setAnthropicKey(e.target.value)}
                      placeholder="sk-ant-admin01-…"
                      style={{ flex: 1, maxWidth: 360, padding: '8px 12px', border: '1px solid #e2e8f0',
                        borderRadius: 8, fontSize: 13, fontFamily: 'monospace' }}
                    />
                    <button onClick={() => setShowApiKey(s => !s)}
                      style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 7,
                        padding: '7px 12px', fontSize: 12, color: '#64748b', cursor: 'pointer' }}>
                      {showApiKey ? 'Hide' : 'Show'}
                    </button>
                    <button onClick={saveIntelligence} disabled={savingIntel || !anthropicKey}
                      style={{ background: anthropicKey ? ORANGE : '#f1f5f9',
                        color: anthropicKey ? '#fff' : '#94a3b8', border: 'none',
                        borderRadius: 8, padding: '7px 16px', fontSize: 13, fontWeight: 600,
                        cursor: anthropicKey ? 'pointer' : 'not-allowed' }}>
                      {savingIntel ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>
                    Stored as <code style={{ fontFamily: 'monospace', background: '#f1f5f9', padding: '1px 5px', borderRadius: 4 }}>ANTHROPIC_ADMIN_KEY</code> in Azure App Settings.
                    Used to query usage and rate limits.
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════
              TAB: PROMPT
          ═══════════════════════════════════════════════════════ */}
          {activeTab === 'prompt' && (
            <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>

              {/* Left — Data Inputs */}
              <div style={{ width: 300, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
                <Card style={{ padding: '18px 18px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: NAVY }}>Data Inputs</div>
                    <SaveBtn saving={savingInputs} saved={savedInputs} onClick={saveDataInputs} label="Save" />
                  </div>

                  {/* Azure section */}
                  <div style={{ marginBottom: 14 }}>
                    <SectionLabel>Azure</SectionLabel>
                    {dataInputs.azure.map(item => (
                      <label key={item.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        gap: 8, padding: '5px 0', cursor: 'pointer' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input type="checkbox" checked={item.checked}
                            onChange={() => toggleDataInput('azure', item.key)}
                            style={{ accentColor: ORANGE, cursor: 'pointer', flexShrink: 0 }} />
                          <span style={{ fontSize: 12, color: item.checked ? '#1e293b' : '#94a3b8' }}>{item.label}</span>
                        </div>
                        <span style={{ fontSize: 10, color: '#cbd5e1', whiteSpace: 'nowrap', flexShrink: 0 }}>~{item.tokens}t</span>
                      </label>
                    ))}
                  </div>

                  {/* Claude API section */}
                  <div style={{ marginBottom: 14, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
                    <SectionLabel>Claude API</SectionLabel>
                    {dataInputs.claude.map(item => (
                      <label key={item.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        gap: 8, padding: '5px 0', cursor: 'pointer' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input type="checkbox" checked={item.checked}
                            onChange={() => toggleDataInput('claude', item.key)}
                            style={{ accentColor: ORANGE, cursor: 'pointer', flexShrink: 0 }} />
                          <span style={{ fontSize: 12, color: item.checked ? '#1e293b' : '#94a3b8' }}>{item.label}</span>
                        </div>
                        <span style={{ fontSize: 10, color: '#cbd5e1', whiteSpace: 'nowrap', flexShrink: 0 }}>~{item.tokens}t</span>
                      </label>
                    ))}
                  </div>

                  {/* Web Search section */}
                  <div style={{ paddingTop: 12, borderTop: '1px solid #f1f5f9', opacity: intelligenceOn ? 1 : 0.5 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <SectionLabel>Web Search Results</SectionLabel>
                    </div>
                    {!intelligenceOn && (
                      <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic', marginBottom: 8 }}>
                        Enable Intelligence features to activate
                      </div>
                    )}
                    {dataInputs.webSearch.map(item => (
                      <label key={item.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        gap: 8, padding: '5px 0', cursor: intelligenceOn ? 'pointer' : 'not-allowed' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input type="checkbox" checked={item.checked} disabled={!intelligenceOn}
                            onChange={() => intelligenceOn && toggleDataInput('webSearch', item.key)}
                            style={{ accentColor: ORANGE, cursor: intelligenceOn ? 'pointer' : 'not-allowed', flexShrink: 0 }} />
                          <span style={{ fontSize: 12, color: item.checked && intelligenceOn ? '#1e293b' : '#94a3b8' }}>{item.label}</span>
                        </div>
                        <span style={{ fontSize: 10, color: '#cbd5e1', whiteSpace: 'nowrap', flexShrink: 0 }}>~{item.tokens}t</span>
                      </label>
                    ))}
                  </div>

                  {/* Token counter */}
                  <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Est. context</span>
                      <span style={{ fontSize: 11, color: NAVY, fontWeight: 700 }}>
                        {tokenCount.toLocaleString()} / {(CONTEXT_WINDOW / 1000).toFixed(0)}k
                      </span>
                    </div>
                    <div style={{ height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden', marginBottom: 6 }}>
                      <div style={{
                        height: '100%', borderRadius: 3, transition: 'width 0.3s',
                        background: tokenCount / CONTEXT_WINDOW > 0.8 ? '#ef4444' : tokenCount / CONTEXT_WINDOW > 0.5 ? ORANGE : '#10b981',
                        width: `${Math.min(100, (tokenCount / CONTEXT_WINDOW) * 100).toFixed(1)}%`,
                      }} />
                    </div>
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>
                      Est. cost per run: ~${((tokenCount / 1000000) * 3.0).toFixed(4)} input +
                      ~${(1500 / 1000000 * 15.0).toFixed(4)} output
                    </div>
                  </div>
                </Card>
              </div>

              {/* Right — Prompt Editor */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>

                {/* System Prompt */}
                <Card>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: NAVY }}>System Prompt</div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => setSystemPrompt(DEFAULT_SYSTEM_PROMPT)}
                        style={{ background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0',
                          borderRadius: 7, padding: '5px 12px', fontSize: 12, cursor: 'pointer' }}>
                        Reset to default
                      </button>
                      <SaveBtn saving={promptSaving} saved={promptSaved} onClick={savePrompt} />
                    </div>
                  </div>
                  <textarea value={systemPrompt} onChange={e => setSystemPrompt(e.target.value)} rows={12}
                    style={{ width: '100%', padding: '12px', border: '1px solid #e2e8f0', borderRadius: 8,
                      fontSize: 12, fontFamily: 'ui-monospace, monospace', lineHeight: 1.6,
                      resize: 'vertical', boxSizing: 'border-box', color: '#1e293b', background: '#f8fafc' }} />
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                    {systemPrompt.length.toLocaleString()} characters
                  </div>
                </Card>

                {/* Report Prompt */}
                <Card>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: NAVY }}>Report Prompt</div>
                    <button onClick={() => setReportPrompt(DEFAULT_REPORT_PROMPT)}
                      style={{ background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0',
                        borderRadius: 7, padding: '5px 12px', fontSize: 12, cursor: 'pointer' }}>
                      Reset to default
                    </button>
                  </div>
                  <textarea value={reportPrompt} onChange={e => setReportPrompt(e.target.value)} rows={8}
                    style={{ width: '100%', padding: '12px', border: '1px solid #e2e8f0', borderRadius: 8,
                      fontSize: 12, fontFamily: 'ui-monospace, monospace', lineHeight: 1.6,
                      resize: 'vertical', boxSizing: 'border-box', color: '#1e293b', background: '#f8fafc' }} />
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                    {reportPrompt.length.toLocaleString()} characters
                  </div>
                </Card>

                {/* Test Prompt */}
                <div style={{ background: '#fff', border: '2px solid #6366f1', borderRadius: 12, padding: '18px 22px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: NAVY }}>Test Prompt</div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Using last run data snapshot</div>
                    </div>
                    <button onClick={runTest} disabled={testRunning}
                      style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8,
                        padding: '8px 18px', fontSize: 13, fontWeight: 600,
                        cursor: testRunning ? 'wait' : 'pointer',
                        display: 'flex', alignItems: 'center', gap: 6 }}>
                      {testRunning ? '⟳ Running…' : '▶ Run Test'}
                    </button>
                  </div>

                  {testRunning && (
                    <div style={{ background: '#f5f3ff', borderRadius: 8, padding: '16px',
                      color: '#6366f1', fontSize: 13, textAlign: 'center' }}>
                      Sending to Claude with last run data…
                    </div>
                  )}

                  {testOutput && !testRunning && (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <StatusBadge status={testOutput.status} />
                        <span style={{ fontSize: 12, color: '#64748b' }}>{testOutput.summary}</span>
                      </div>
                      <pre style={{ background: '#0f172a', color: '#e2e8f0', borderRadius: 8,
                        padding: '14px 16px', fontSize: 11, fontFamily: 'ui-monospace, monospace',
                        lineHeight: 1.6, overflow: 'auto', maxHeight: 340, margin: 0 }}>
                        {JSON.stringify(testOutput, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════
              TAB: RUN HISTORY
          ═══════════════════════════════════════════════════════ */}
          {activeTab === 'history' && (
            <div style={{ maxWidth: 1000 }}>
              <Card style={{ padding: 0, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                      {['Date & Time', 'Status', 'Alerts', 'Daily Cost', 'Email Sent', 'Action'].map(h => (
                        <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 10,
                          fontWeight: 700, color: '#64748b', textTransform: 'uppercase',
                          letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {runs.length === 0 && (
                      <tr>
                        <td colSpan={6} style={{ padding: '60px 0', textAlign: 'center', color: '#94a3b8' }}>
                          No runs yet — trigger a run or wait for the next scheduled run
                        </td>
                      </tr>
                    )}
                    {runs.slice(0, 30).map((run, i) => (
                      <tr key={run.run_id || i}
                        style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                        <td style={{ padding: '12px 16px', color: '#1e293b', whiteSpace: 'nowrap' }}>
                          {fmtDateShort(run.run_at)}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <StatusBadge status={run.status} />
                        </td>
                        <td style={{ padding: '12px 16px', color: '#64748b' }}>
                          {run.alert_count > 0 ? (
                            <span style={{ fontWeight: 600, color: run.alert_count > 2 ? '#b91c1c' : '#b45309' }}>
                              {run.alert_count}
                            </span>
                          ) : (
                            <span style={{ color: '#94a3b8' }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', color: '#64748b' }}>
                          {run.daily_cost_usd != null ? `$${Number(run.daily_cost_usd).toFixed(2)}` : '—'}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          {run.email_sent ? (
                            <span style={{ color: '#15803d', fontSize: 12 }}>✓ Sent</span>
                          ) : (
                            <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <button onClick={() => openRunDetail(run)}
                            style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 6,
                              padding: '4px 12px', fontSize: 12, color: NAVY, cursor: 'pointer',
                              fontWeight: 500 }}>
                            View →
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </div>
          )}
        </div>

        {/* ── Run Now sticky button ── */}
        <div style={{ position: 'fixed', bottom: 28, right: 32, display: 'flex', flexDirection: 'column',
          alignItems: 'flex-end', gap: 10, zIndex: 50 }}>
          <button onClick={handleRunNow} disabled={runLoading}
            style={{ background: runLoading ? '#f1f5f9' : ORANGE, color: runLoading ? '#94a3b8' : '#fff',
              border: 'none', borderRadius: 12, padding: '13px 24px', fontSize: 14, fontWeight: 700,
              cursor: runLoading ? 'wait' : 'pointer',
              boxShadow: '0 4px 20px rgba(243,116,40,0.35)',
              display: 'flex', alignItems: 'center', gap: 8, transition: 'all 0.2s' }}>
            {runLoading ? '⟳ Running…' : '▶ Run Now'}
          </button>
        </div>

        {/* ── Run Detail Modal ── */}
        {viewRun && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 24 }}
            onClick={() => setViewRun(null)}>
            <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 760,
              maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' }}
              onClick={e => e.stopPropagation()}>

              {/* Modal header */}
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                padding: '24px 28px 16px', borderBottom: '1px solid #e2e8f0' }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: NAVY, marginBottom: 4 }}>Run Detail</div>
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>{fmtDateShort(viewRun.run_at)}</div>
                  {viewRun.status && (
                    <div style={{ marginTop: 8 }}><StatusBadge status={viewRun.status} /></div>
                  )}
                </div>
                <button onClick={() => setViewRun(null)}
                  style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 20,
                    cursor: 'pointer', padding: 4 }}>✕</button>
              </div>

              {/* Modal body */}
              <div style={{ padding: '20px 28px 28px' }}>
                {viewRunLoading && (
                  <div style={{ textAlign: 'center', color: '#94a3b8', padding: '40px 0' }}>Loading run details…</div>
                )}

                {viewRunData && !viewRunLoading && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

                    {/* Alerts */}
                    {viewRunData.alerts_json && viewRunData.alerts_json.length > 0 && (
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase',
                          letterSpacing: '0.06em', marginBottom: 10 }}>Alerts</div>
                        {viewRunData.alerts_json.map((alert, i) => (
                          <div key={i} style={{
                            background: alert.severity === 'critical' ? '#fef2f2' : '#fffbeb',
                            border: `1px solid ${alert.severity === 'critical' ? '#fca5a5' : '#fcd34d'}`,
                            borderRadius: 8, padding: '10px 14px', marginBottom: 8,
                            fontSize: 13, color: alert.severity === 'critical' ? '#b91c1c' : '#b45309',
                          }}>
                            <strong>{alert.title || alert.metric || ''}</strong>{alert.value ? `: ${alert.value}` : ''}{(alert.detail || alert.message) ? ` — ${alert.detail || alert.message}` : ''}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Claude output */}
                    {viewRunData.claude_output && (
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase',
                          letterSpacing: '0.06em', marginBottom: 10 }}>AI Analysis</div>
                        {viewRunData.claude_output.summary && (
                          <div style={{ fontSize: 13, color: '#1e293b', marginBottom: 12, lineHeight: 1.6,
                            padding: '10px 14px', background: '#f8fafc', borderRadius: 8 }}>
                            {viewRunData.claude_output.summary}
                          </div>
                        )}
                        {viewRunData.claude_output.recommendations && viewRunData.claude_output.recommendations.length > 0 && (
                          <div style={{ marginBottom: 12 }}>
                            <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 6 }}>Recommendations</div>
                            {viewRunData.claude_output.recommendations.map((r, i) => (
                              <div key={i} style={{ fontSize: 13, color: '#475569', padding: '6px 0',
                                borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 8 }}>
                                <span style={{ color: ORANGE, flexShrink: 0 }}>→</span> {r}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Raw metrics */}
                    {viewRunData.metrics_json && (
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase',
                          letterSpacing: '0.06em', marginBottom: 10 }}>Raw Metrics</div>
                        <pre style={{ background: '#0f172a', color: '#e2e8f0', borderRadius: 8,
                          padding: '14px 16px', fontSize: 11, fontFamily: 'ui-monospace, monospace',
                          lineHeight: 1.6, overflow: 'auto', maxHeight: 300, margin: 0 }}>
                          {JSON.stringify(viewRunData.metrics_json, null, 2)}
                        </pre>
                      </div>
                    )}

                    {viewRunData.error && (
                      <div style={{ color: '#b91c1c', fontSize: 13 }}>Error loading detail: {viewRunData.error}</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}
